'use client';
import clsx from 'clsx';
import type {
  ComponentPropsWithoutRef,
  FunctionComponent,
  PropsWithChildren,
  ReactElement,
  ReactPortal,
} from 'react';

import { type ButtonProps as Btn, getButtonClasses } from './Button.type';
import { ButtonContent } from './ButtonContent';

import './Button.css';

export interface ButtonProps extends Btn, Omit<ComponentPropsWithoutRef<'button'>, 'color'> {
  component?: 'button';
}

export interface ButtonAnchorProps extends Btn, Omit<ComponentPropsWithoutRef<'a'>, 'color'> {
  component: 'a';
  disabled?: boolean;
}

export interface FakeButtonProps extends Btn, Omit<ComponentPropsWithoutRef<'span'>, 'color'> {
  component: 'span' | 'div';
  disabled?: boolean;
}
// For Button with React Router Link or similar components
export interface ReactButtonProps
  extends Btn,
    Omit<ComponentPropsWithoutRef<'a'>, 'href' | 'color'> {
  component: ReactElement | ReactPortal;
  disabled?: boolean;
}

type AnyButtonProps = ButtonProps | ButtonAnchorProps | FakeButtonProps | ReactButtonProps;

export function useButton({ component, disabled, ...props }: AnyButtonProps) {
  if (component === 'a' || 'href' in props || typeof component === 'object') {
    return {
      attrs: {
        'data-name': 'ButtonAnchor',
        'aria-disabled': disabled ? 'true' : undefined,
        ...props,
      },
      Cmp: 'a' as unknown as FunctionComponent<PropsWithChildren<any>>,
    };
  }

  if (component === 'span' || component === 'div') {
    return {
      attrs: {
        'data-name': 'FakeButton',
        'aria-disabled': disabled ? 'true' : undefined,
        ...props,
      },
      Cmp: 'span' as unknown as FunctionComponent<PropsWithChildren<any>>,
    };
  }

  return {
    attrs: {
      'data-name': 'Button',
      type: 'button',
      disabled,
      ...props,
    },
    Cmp: 'button' as unknown as FunctionComponent<PropsWithChildren<any>>,
  };
}

// Main Button component that handles both button and anchor functionality
export const Button: FunctionComponent<AnyButtonProps> = ({
  className,
  children,
  color = 'saffron',
  icon,
  iconWidth,
  size = 'medium',
  theme = 'solid',
  variant = 'pill',
  ...props
}) => {
  const { Cmp, attrs } = useButton(props);

  return (
    <Cmp {...attrs} className={clsx(getButtonClasses({ color, size, theme, variant }), className)}>
      <ButtonContent icon={icon} iconWidth={iconWidth}>
        {children}
      </ButtonContent>
    </Cmp>
  );
};

Button.displayName = 'Button';
